import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { deleteSMS } from 'services/api/WorkflowServiceAPI';
import { useGetPartyStages, useGetSMSData, useGetSmsStatistics } from 'services/hooks/queries';
import { BulkActions, SMSTable, SMSDetailsModal } from '../components';
import type { SMSLead } from '../components';
import { SMSAnalyticsFilters, SMSStatusSummary, SMS_ANALYTICS_STATUS_OPTIONS } from '../components/SMSAnalytics';
import { Pagination } from 'common/components';
import { SMSConversationState, SMSNodeStatus, SMSNodeSubStatus, SMSStatus } from 'types/sms.type';
import type {
    AgenticWorkflowOutletContext,
    HandleDeleteCallsParams,
    HandleExportCSVParams,
} from '../AgenticWorkflowDetail.types';
import { downloadCsv, setDateTimes } from '../utils';
import { useSMSAnalyticsState } from './hooks/useSMSAnalyticsState';

import styles from '../AgenticWorkflowDetail.module.scss';

const SMSAnalyticsPageNew: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { initialStartDate, initialEndDate, openNotification, closeNotification } =
        useOutletContext<AgenticWorkflowOutletContext>();

    const {
        state,
        handleSearchChange,
        handleSetDateRange,
        handleDateRangeTypeChange,
        handleFilterChange,
        handleSortChange,
        handlePageChange,
        handleRowsPerPageChange,
        syncPagination,
        clampPageToTotal,
        handleCheckboxChange,
        handleSelectAllCheckboxChange,
        clearSelection,
        handleClearFilters,
    } = useSMSAnalyticsState(initialStartDate, initialEndDate);

    const {
        dateRange,
        dateRangeType,
        filterLeadStatus,
        filterSMSStatus,
        filterSMSDirection,
        filterHasResponse,
        smsSortField,
        sortDirection,
        selectedIds,
        searchTerm,
        currentPage,
        rowsPerPage,
    } = state;

    const [selectedSms, setSelectedSms] = useState<SMSLead | null>(null);

    const selectedStatusQueryParams = useMemo(() => {
        const baseParams = {
            smsStatus: '',
            nodeStatus: '',
            conversationState: '',
            nodeSubStatus: '',
        };
        if (!filterSMSStatus) return baseParams;

        if (filterSMSStatus in SMSStatus) return { ...baseParams, smsStatus: filterSMSStatus };
        if (filterSMSStatus in SMSNodeStatus) return { ...baseParams, nodeStatus: filterSMSStatus };
        if (filterSMSStatus in SMSConversationState) return { ...baseParams, conversationState: filterSMSStatus };
        if (filterSMSStatus in SMSNodeSubStatus) return { ...baseParams, nodeSubStatus: filterSMSStatus };

        return baseParams;
    }, [filterSMSStatus]);

    const { startWithTime, endWithTime } = setDateTimes(dateRange);

    const smsQueryParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        ...selectedStatusQueryParams,
        hasResponse: filterHasResponse,
        partyStage: filterLeadStatus,
        search: searchTerm,
        perPage: rowsPerPage,
        sortOrder: sortDirection,
        sortBy: smsSortField,
        currentPage,
    };

    const smsStatisticsParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        partyStage: filterLeadStatus,
        smsStatus: filterSMSStatus in SMSStatus ? filterSMSStatus : '',
        search: searchTerm,
    };

    const { partyStages } = useGetPartyStages();
    const { smsStatistics, refetchSmsStatistics } = useGetSmsStatistics(smsStatisticsParams);
    const { smsData, isSMSDataFetching, refetch: refetchSmsData } = useGetSMSData(smsQueryParams, true);

    const smsLeads = useMemo<SMSLead[]>(() => {
        if (!smsData?.data?.length) return [];
        return smsData.data.map((sms) => {
            const smsDateTime = new Date(sms.sentTime || sms.scheduledDate);
            return {
                id: sms.crmId,
                smsId: sms.smsId,
                conversationId: sms.conversationId || sms.smsId,
                smsDate: smsDateTime.toLocaleDateString('en-US'),
                smsTime: smsDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                status: sms.nodeStatus || sms.status || sms.nodeSubStatus || sms.conversationState || 'QUEUED',
                crmStatus: sms.partyStage || 'NEW_LEAD',
                crmId: sms.crmId,
                name: sms.contactName || 'Unknown',
                number: sms.phoneNumber || 'Unknown',
                messageDirection: sms.messageDirection,
                disposition: sms.disposition,
                deliveredTime: sms.deliveredTime,
                failureReason: sms.failureReason,
            };
        });
    }, [smsData]);

    const filteredSmsLeads = useMemo<SMSLead[]>(() => {
        if (!filterSMSDirection) return smsLeads;
        return smsLeads.filter((sms) => sms.messageDirection === filterSMSDirection);
    }, [smsLeads, filterSMSDirection]);

    const totalPages = smsData?.pagination?.totalPages || 1;
    const currentPageFromAPI = smsData?.pagination?.currentPage || currentPage;
    const perPageFromAPI = smsData?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        syncPagination(smsData?.pagination);
    }, [smsData?.pagination, syncPagination]);

    useEffect(() => {
        clampPageToTotal(currentPage, totalPages);
    }, [totalPages, currentPage, clampPageToTotal]);

    const handleDeleteSMS = async ({ selectedCallId }: HandleDeleteCallsParams) => {
        if (!selectedCallId && !selectedIds.length) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one SMS to delete.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        openNotification({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${selectedCallId ? 'this SMS' : `${selectedIds.length} SMS message(s)`}?`,
            type: 'error',
            body: null,
            buttons: [
                { label: 'Cancel', onClick: closeNotification, style: { backgroundColor: '#9b9b9b' } },
                {
                    label: 'Delete',
                    onClick: async () => {
                        closeNotification();
                        try {
                            const idsToDelete = selectedCallId
                                ? [selectedCallId]
                                : filteredSmsLeads
                                      .filter((sms) => selectedIds.includes(sms.conversationId))
                                      .map((sms) => sms.smsId);
                            await deleteSMS(idsToDelete);
                            openNotification({
                                isOpen: true,
                                title: 'Deletion Successful',
                                message: 'SMS messages deleted successfully',
                                type: 'success',
                                body: null,
                                buttons: [],
                            });
                            await refetchSmsStatistics();
                            await refetchSmsData();
                            clearSelection();
                            setSelectedSms(null);
                        } catch (err) {
                            console.error('Error deleting SMS:', err);
                            openNotification({
                                isOpen: true,
                                title: 'Error',
                                message: `Failed to delete SMS: ${(err as Error).message || 'Unknown error'}`,
                                type: 'error',
                                body: null,
                                buttons: [],
                            });
                        }
                    },
                    style: { backgroundColor: '#f44336' },
                },
            ],
        });
    };

    const handleExportCSV = ({ selectedCallId }: HandleExportCSVParams = {}) => {
        if (!selectedIds.length && !selectedCallId) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one SMS to export.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        const selectedLeads = selectedCallId
            ? filteredSmsLeads.filter((sms) => sms.smsId === selectedCallId)
            : filteredSmsLeads.filter((sms) => selectedIds.includes(sms.conversationId));
        const headers = ['SMS ID', 'SMS Date', 'SMS Time', 'Status', 'Party Status', 'CRM ID', 'Name', 'Number'];
        const csvRows = selectedLeads.map((sms) => [
            sms.smsId,
            sms.smsDate,
            sms.smsTime,
            sms.status,
            sms.crmStatus,
            sms.crmId,
            sms.name,
            sms.number,
        ]);
        const dateString = new Date().toISOString().split('T')[0];
        downloadCsv(headers, csvRows, `workflow_sms_${dateString}.csv`);
        openNotification({
            isOpen: true,
            title: 'Export Complete',
            message: `Successfully exported ${selectedLeads.length} SMS message(s) to CSV.`,
            type: 'success',
            body: null,
            buttons: [],
        });
        if (!selectedCallId) clearSelection();
    };

    const hasActiveFilters = !!(
        searchTerm.trim() ||
        filterLeadStatus ||
        filterSMSStatus ||
        filterSMSDirection ||
        filterHasResponse ||
        dateRangeType !== 'today'
    );

    return (
        <div className='flex flex-col gap-2xl' data-testid='sms-analytics-page-new'>
            <SMSAnalyticsFilters
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                filterSMSStatus={filterSMSStatus}
                filterLeadStatus={filterLeadStatus}
                filterSMSDirection={filterSMSDirection}
                filterHasResponse={filterHasResponse}
                partyStages={partyStages?.data ?? []}
                onSearchChange={handleSearchChange}
                onDateRangeTypeChange={handleDateRangeTypeChange}
                onDateRangeChange={handleSetDateRange}
                onFilterChange={handleFilterChange}
                onClear={handleClearFilters}
            />

            <SMSStatusSummary smsStats={smsStatistics} />

            <div>
                <SMSTable
                    data={filteredSmsLeads}
                    selectedIds={selectedIds}
                    sortField={smsSortField}
                    sortDirection={sortDirection}
                    filterLeadStatus={filterLeadStatus}
                    filterSMSStatus={filterSMSStatus}
                    partyStages={partyStages?.data ?? []}
                    smsStatusOptions={SMS_ANALYTICS_STATUS_OPTIONS}
                    onSortChange={handleSortChange}
                    onFilterLeadStatus={(value) => handleFilterChange('leadStatus', value)}
                    onFilterSMSStatus={(value) => handleFilterChange('smsStatus', value)}
                    onSelectAllCheckboxChange={(event) => handleSelectAllCheckboxChange(event, filteredSmsLeads)}
                    onCheckboxChange={handleCheckboxChange}
                    onSmsDetails={setSelectedSms}
                    showColumnFilters={false}
                    dateRangeConfig={{
                        type: dateRangeType,
                        onTypeChange: handleDateRangeTypeChange,
                        setDateRange: handleSetDateRange,
                    }}
                    isLoading={isSMSDataFetching}
                    hasActiveFilters={hasActiveFilters}
                />
                <div className='flex justify-between items-center flex-wrap gap-sm'>
                    <BulkActions
                        selectedCalls={selectedIds}
                        handleDeleteCalls={handleDeleteSMS}
                        handleExportCSV={handleExportCSV}
                    />
                    <Pagination
                        currentPage={currentPageFromAPI}
                        totalPages={totalPages}
                        rowsPerPage={perPageFromAPI}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className={styles.agenticPagination}
                    />
                </div>
            </div>

            <SMSDetailsModal
                sms={selectedSms}
                onClose={() => setSelectedSms(null)}
                onDelete={handleDeleteSMS}
                onExportCSV={handleExportCSV}
            />
        </div>
    );
};

export default SMSAnalyticsPageNew;
